import { getRebuyApiKey, getRebuyVisitorUuid } from './rebuy-helpers.js';

class FeaturedCollectionSource extends HTMLElement {
  static responseCache = new Map();

  constructor() {
    super();

    this.loaded = false;
  }

  connectedCallback() {
    this.section = this.closest('.section--featured-collections');
    this.tabbedContent = this.closest('tabbed-content');
    this.tabPanel = this.closest('tab-panel');
    this.tabButton = this.tabPanel
      ? document.getElementById(this.tabPanel.getAttribute('aria-labelledby'))
      : null;
    this.grid = this.querySelector('[data-items]');

    if (this.loaded || this.dataset.source !== 'rebuy_recently_viewed' || !this.grid) {
      return;
    }

    this.loadRecentlyViewed().catch((error) => {
      console.error('[FeaturedCollectionSource] Rebuy recently viewed error', error);
      this.hideWhenEmpty();
    });
  }

  async loadRecentlyViewed() {
    const apiKey = getRebuyApiKey();
    const uuid = getRebuyVisitorUuid();
    const customerId = this.dataset.customerId || '';
    const countryCode = this.dataset.countryCode || '';
    const limit = Math.min(Number(this.dataset.limit || '0'), 20);

    if (!apiKey || !limit || (!uuid && !customerId)) {
      this.hideWhenEmpty();
      return;
    }

    const cacheKey = [this.dataset.source, limit, uuid || 'anonymous', customerId, countryCode].join(':');
    let products = FeaturedCollectionSource.responseCache.get(cacheKey);

    if (!products) {
      products = await this.fetchRecentlyViewed({ apiKey, uuid, customerId, countryCode, limit });
      FeaturedCollectionSource.responseCache.set(cacheKey, products);
    }

    const handles = this.getRenderableHandles(products);

    if (!handles.length) {
      this.hideWhenEmpty();
      return;
    }

    const responseMarkup = await this.fetchRenderedCards(handles);

    if (!responseMarkup.trim()) {
      this.hideWhenEmpty();
      return;
    }

    this.grid.innerHTML = responseMarkup;
    this.syncOverflowState();
    this.syncGridAlignment();
    this.revealCardsIfNeeded();
    this.loaded = true;
  }

  async fetchRecentlyViewed({ apiKey, uuid, customerId, countryCode, limit }) {
    const params = new URLSearchParams();

    params.set('key', apiKey);
    params.set('limit', String(limit));
    params.set('subject', 'user');
    params.set('verb', 'viewed');

    if (uuid) params.set('uuid', uuid);
    if (customerId) params.set('shopify_customer_id', customerId);
    if (countryCode) params.set('country_code', countryCode);

    const response = await fetch(`https://rebuyengine.com/api/v1/products/viewed?${params.toString()}`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Viewed endpoint request failed with ${response.status}`);
    }

    const json = await response.json();

    return Array.isArray(json.data) ? json.data : [];
  }

  getRenderableHandles(products) {
    const currentProductId = String(this.dataset.currentProductId || '0');
    const limit = Number(this.dataset.limit || '0');
    const seen = new Set();
    const handles = [];

    for (const product of products) {
      const handle =
        product.handle ||
        product.product_handle ||
        product.url?.split('/products/').pop()?.split('?')[0];
      const productId = String(
        product.shopify_product_id ||
          product.id ||
          product.admin_graphql_api_id?.split('/').pop() ||
          '',
      );
      const tags = Array.isArray(product.tags)
        ? product.tags.join(',').toLowerCase()
        : String(product.tags || '').toLowerCase();
      const dedupeKey = productId || handle;

      if (!handle || !dedupeKey || seen.has(dedupeKey)) continue;
      if (currentProductId !== '0' && productId === currentProductId) continue;
      if (tags.includes('coffee archive') || tags.includes('product archive')) continue;

      seen.add(dedupeKey);
      handles.push(handle);

      if (handles.length >= limit) break;
    }

    return handles;
  }

  async fetchRenderedCards(handles) {
    const query = [
      this.dataset.sectionId || '',
      this.dataset.blockId || '',
      this.dataset.grid || '3',
      this.dataset.gridMobile || '1',
      this.dataset.carouselMobileEnabled || '0',
      this.dataset.quickShopTrigger || 'none',
      this.dataset.smallButtonsOnMobile || '0',
      this.dataset.currentProductId || '0',
      `items_scroll_enabled:${this.dataset.itemsScrollEnabled === '1' ? '1' : '0'}`,
      ...handles,
    ].join('+');

    const params = new URLSearchParams();
    params.set('view', 'rebuy-featured-collection');
    params.set('q', query);

    const response = await fetch(`/search?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Rendered card request failed with ${response.status}`);
    }

    const markup = document.createElement('div');
    markup.innerHTML = await response.text();

    const responseContainer = markup.querySelector('[data-featured-collection-response]');

    return responseContainer ? responseContainer.innerHTML : '';
  }

  syncGridAlignment() {
    const cardCount = this.grid.querySelectorAll('.product-card').length;
    const grid = Number(this.dataset.grid || '0');

    this.grid.classList.toggle('grid-layout--align-center', cardCount > 0 && grid > cardCount);
  }

  syncOverflowState() {
    const scroller = this.grid?.closest('items-scroll');

    if (!scroller) return;

    const cardCount = this.grid.querySelectorAll('.product-card').length;
    const grid = Number(this.dataset.grid || '0');
    const gridMobile = Number(this.dataset.gridMobile || '0');
    const carouselDesktopEnabled = this.dataset.carouselDesktopEnabled === '1';
    const carouselMobileEnabled = this.dataset.carouselMobileEnabled === '1';
    const breakpoint = this.dataset.carouselBreakpoint || 'phab';

    scroller.removeAttribute('items-overflow');
    scroller.removeAttribute('items-overflow-max');
    scroller.removeAttribute('items-overflow-min');

    if (carouselMobileEnabled && carouselDesktopEnabled) {
      if (cardCount > grid) {
        scroller.setAttribute('items-overflow', '');
      } else if (cardCount > gridMobile) {
        scroller.setAttribute('items-overflow-max', breakpoint);
      }
      return;
    }

    if (carouselMobileEnabled && !carouselDesktopEnabled) {
      if (cardCount > gridMobile) {
        scroller.setAttribute('items-overflow-max', breakpoint);
      }
      return;
    }

    if (!carouselMobileEnabled && carouselDesktopEnabled && cardCount > grid) {
      scroller.setAttribute('items-overflow-min', breakpoint);
    }
  }

  revealCardsIfNeeded() {
    if (document.body.dataset.animLoad !== 'true' || typeof window.sr === 'undefined') {
      return;
    }

    const selectedPanel = this.tabPanel || this;

    if (selectedPanel.hasAttribute('hidden')) return;

    this.grid.querySelectorAll('.product-card-top').forEach((card) => {
      window.sr.reveal(card, {
        container: this.grid,
        origin: 'bottom',
        delay: 16,
        reset: true,
      });
    });
  }

  hideWhenEmpty() {
    if (!this.section) return;

    if (!this.tabButton || !this.tabPanel) {
      this.section.hidden = true;
      return;
    }

    const selected = this.tabButton.getAttribute('aria-selected') === 'true';

    this.tabButton.hidden = true;
    this.tabButton.setAttribute('aria-selected', 'false');
    this.tabButton.setAttribute('tabindex', '-1');
    this.tabPanel.setAttribute('hidden', '');

    this.syncTabbedContentState();

    const visibleTabs = this.getVisibleTabs();

    if (!visibleTabs.length) {
      this.section.hidden = true;
      return;
    }

    if (selected) {
      visibleTabs[0].click();
      if (this.tabbedContent) {
        this.tabbedContent.currentTab = 0;
      }
    }
  }

  getVisibleTabs() {
    if (!this.section) return [];

    return Array.from(this.section.querySelectorAll('tab-triggers button')).filter(
      (button) => !button.hidden,
    );
  }

  syncTabbedContentState() {
    if (!this.tabbedContent) return;

    this.tabbedContent.tabs = this.getVisibleTabs();
    this.tabbedContent.tabPanels = Array.from(this.tabbedContent.querySelectorAll('tab-panel')).filter(
      (panel) => {
        const button = document.getElementById(panel.getAttribute('aria-labelledby'));

        return button && !button.hidden;
      },
    );

    const selectedIndex = Array.from(this.tabbedContent.tabs).findIndex(
      (button) => button.getAttribute('aria-selected') === 'true',
    );

    this.tabbedContent.currentTab = selectedIndex >= 0 ? selectedIndex : 0;
  }
}

if (!customElements.get('featured-collection-source')) {
  customElements.define('featured-collection-source', FeaturedCollectionSource);
}
